import { Component, HostListener, OnDestroy, OnInit, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Subject, filter, takeUntil } from 'rxjs';
import { AuditService } from '../shared/services/audit.service';
import { AgentCatalogService } from '../shared/services/agent-catalog.service';
import { CatalogAgent } from '../shared/models/agent-catalog.model';
import { AbilitiesModalComponent } from '../shared/components/abilities-modal/abilities-modal.component';
import { WhatsNewModalComponent } from '../shared/components/whats-new-modal/whats-new-modal.component';
import { GuidedTourComponent } from '../shared/components/guided-tour/guided-tour.component';
import { ConnectionsModalComponent } from '../shared/components/connections-modal/connections-modal.component';

interface QuickChatGroup {
  letter: string;
  agentId: string;
  agentName: string;
  description: string;
  command: string;
  chats: { label: string; prompt: string }[];
}

/** One action matched by a search, carrying the agent it came from so the
 *  flat results list can still say where each hit belongs. */
interface CatalogHit {
  agentId: string;
  agentName: string;
  command: string;
  label: string;
  prompt: string;
}

@Component({
  selector: 'app-layout-shell',
  standalone: true,
  imports: [
    CommonModule, RouterOutlet, AbilitiesModalComponent, WhatsNewModalComponent,
    GuidedTourComponent, ConnectionsModalComponent,
  ],
  templateUrl: './layout-shell.component.html',
  styleUrls: ['./layout-shell.component.scss']
})
export class LayoutShellComponent implements OnInit, OnDestroy {
  catalogOpen = signal<boolean>(false);
  catalogSearch = signal<string>('');
  catalogAgents = signal<CatalogAgent[]>([]);
  /** Agent pinned in the sidebar; null = fall back to the first one listed. */
  catalogSelected = signal<string | null>(null);
  private readonly destroying$ = new Subject<void>();

  readonly catalogQuery = computed(() => this.catalogSearch().toLowerCase().trim());

  /** Every catalog agent, alphabetical — the sidebar's browse list. */
  readonly catalogGroups = computed<QuickChatGroup[]>(() =>
    this.catalogAgents()
      .map(agent => ({
        letter: agent.displayName[0]?.toUpperCase() ?? '#',
        agentId: agent.agentId,
        agentName: agent.displayName,
        description: agent.description ?? '',
        command: agent.command,
        chats: agent.quickChats ?? [],
      }))
      .sort((a, b) => a.agentName.localeCompare(b.agentName)));

  readonly totalActions = computed(() =>
    this.catalogGroups().reduce((n, g) => n + g.chats.length, 0));

  /** Sidebar contents: agents whose name, command, description or any action
   *  matches the search. */
  readonly filteredCatalogGroups = computed<QuickChatGroup[]>(() => {
    const q = this.catalogQuery();
    if (!q) return this.catalogGroups();
    return this.catalogGroups().filter(g =>
      g.agentName.toLowerCase().includes(q) ||
      g.command.toLowerCase().includes(q) ||
      g.description.toLowerCase().includes(q) ||
      g.chats.some(c => c.label.toLowerCase().includes(q) || c.prompt.toLowerCase().includes(q)));
  });

  /** Search view: every matching action across every agent, flattened. An
   *  agent-level hit (name or command) contributes all of its actions, so
   *  searching an agent's name surfaces its whole menu. */
  readonly catalogResults = computed<CatalogHit[]>(() => {
    const q = this.catalogQuery();
    if (!q) return [];
    const hits: CatalogHit[] = [];
    for (const g of this.catalogGroups()) {
      const agentHit = g.agentName.toLowerCase().includes(q) || g.command.toLowerCase().includes(q);
      for (const c of g.chats) {
        if (agentHit || c.label.toLowerCase().includes(q) || c.prompt.toLowerCase().includes(q)) {
          hits.push({ agentId: g.agentId, agentName: g.agentName, command: g.command, label: c.label, prompt: c.prompt });
        }
      }
    }
    return hits;
  });

  /** Browse view: the pinned agent, or the first one listed. Falling back here
   *  (rather than resetting the pin whenever the list changes) keeps the right
   *  pane populated without extra bookkeeping. */
  readonly activeGroup = computed<QuickChatGroup | null>(() => {
    const groups = this.filteredCatalogGroups();
    if (!groups.length) return null;
    const id = this.catalogSelected();
    return groups.find(g => g.agentId === id) ?? groups[0];
  });

  constructor(
    private readonly router: Router,
    private readonly audit: AuditService,
    private readonly catalogSvc: AgentCatalogService,
  ) {}

  ngOnInit(): void {
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      takeUntil(this.destroying$)
    ).subscribe(e => this.audit.trackEvent({ action_type: 'PAGE_VIEW', description: `Navigated to ${e.urlAfterRedirects}` }));

    // Exclude embedded apps (outputType === 'app') from the command palette —
    // they open from the Apps Portal (wg-agents-apps-ui) via the sidebar tile,
    // not via a chat slash-command.
    this.catalogSvc.getCatalog().subscribe(agents =>
      this.catalogAgents.set(agents.filter(a => (a.outputType ?? '').toLowerCase() !== 'app')));
    this.catalogSvc.openCatalog$.pipe(takeUntil(this.destroying$)).subscribe(() => this.showCatalog());
  }

  openCatalog(event: Event): void {
    event.stopPropagation();
    this.showCatalog();
  }

  private showCatalog(): void {
    this.catalogSearch.set('');
    this.catalogSelected.set(null);
    this.catalogOpen.set(true);
    setTimeout(() => {
      document.querySelector<HTMLInputElement>('.cat-search__input')?.focus();
    }, 60);
  }

  closeCatalog(): void {
    this.catalogOpen.set(false);
    this.catalogSearch.set('');
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.catalogOpen()) this.closeCatalog();
  }

  onCatalogSearchInput(event: Event): void {
    this.catalogSearch.set((event.target as HTMLInputElement).value);
  }

  clearCatalogSearch(): void {
    this.catalogSearch.set('');
    document.querySelector<HTMLInputElement>('.cat-search__input')?.focus();
  }

  /** Sidebar pick. Searching is a different mode (flat results across every
   *  agent), so choosing an agent drops the query and browses that agent —
   *  otherwise the click would look like it did nothing. */
  selectCatalogAgent(agentId: string): void {
    this.catalogSearch.set('');
    this.catalogSelected.set(agentId);
    document.querySelector('.cat-main')?.scrollTo({ top: 0 });
  }

  onQuickChatClick(command: string, prompt: string): void {
    this.closeCatalog();
    this.catalogSvc.selectCommand(`${command} ${prompt}`);
    this.router.navigate(['/chat']);
  }

  /** Load just the command into the chat input (trailing space) so the user can
   *  type their own message after it — no preset prompt to backspace. */
  onCommandClick(command: string): void {
    this.closeCatalog();
    this.catalogSvc.selectCommand(`${command} `);
    this.router.navigate(['/chat']);
  }

  stop(event: Event): void { event.stopPropagation(); }

  ngOnDestroy(): void { this.destroying$.next(); this.destroying$.complete(); }
}
